import React from "react";
import dayjs from "dayjs";
import { formatFlightTime } from "../../../../utils/timezoneUtils";
import { joinClassNames } from "../utils/classNames";
import { useGridTooltipActions } from "../hooks/useGridTooltip";

const DEFAULT_TIMEZONE_MODE = "UTC";

const DIRECTION_CONFIG = Object.freeze({
    dep: {
        airportProp: "departureAirport",
        scheduledLabel: "STD",
        estimatedLabel: "ETD",
        actualLabel: "ATD",
        scheduledProp: "std",
        estimatedProp: "etd",
        actualProp: "atd",
    },
    arr: {
        airportProp: "arrivalAirport",
        scheduledLabel: "STA",
        estimatedLabel: "ETA",
        actualLabel: "ATA",
        scheduledProp: "sta",
        estimatedProp: "eta",
        actualProp: "ata",
    },
});

function normalizeTimezoneInput(timezoneMode, airportsMap) {
    if (timezoneMode && typeof timezoneMode === "object") {
        return {
            mode: timezoneMode.timeZone || timezoneMode.timezoneMode || timezoneMode.mode || DEFAULT_TIMEZONE_MODE,
            airportsMap: timezoneMode.airportsMap || airportsMap || {},
        };
    }

    return {
        mode: timezoneMode || DEFAULT_TIMEZONE_MODE,
        airportsMap: airportsMap || {},
    };
}

function pickPrimaryTime(config, props) {
    const actualValue = props[config.actualProp];
    const estimatedValue = props[config.estimatedProp];
    const scheduledValue = props[config.scheduledProp];

    if (actualValue) {
        return { label: config.actualLabel, value: actualValue, kind: "actual", scheduledValue };
    }
    if (estimatedValue) {
        return { label: config.estimatedLabel, value: estimatedValue, kind: "estimated", scheduledValue };
    }
    return { label: config.scheduledLabel, value: scheduledValue, kind: "scheduled", scheduledValue };
}

function getArrivalBaseUtcTime(props) {
    return props.atd || props.etd || props.std || null;
}

function formatMinuteDiff(value, scheduledValue) {
    if (!value || !scheduledValue) return "";
    const minutes = dayjs(value).diff(dayjs(scheduledValue), "minute");
    if (!Number.isFinite(minutes) || minutes === 0) return "";
    return `${minutes > 0 ? "+" : ""}${minutes}m`;
}

function formatTime(value, airportCode, timezoneMode, airportsMap, baseUtcTime, format = "HH:mm") {
    if (!value) return { time: "--:--", crossDayMark: "", label: "", error: false };
    const result = formatFlightTime(value, airportCode, timezoneMode, airportsMap, baseUtcTime, format);
    return {
        time: result.time || "--:--",
        crossDayMark: result.crossDayMark || "",
        label: result.label || "",
        error: Boolean(result.error || result.isInvalid),
    };
}

function buildTooltipRows(config, props, airportCode, timezoneMode, airportsMap, baseUtcTime) {
    return [
        { label: config.scheduledLabel, value: props[config.scheduledProp], kind: "scheduled" },
        { label: config.estimatedLabel, value: props[config.estimatedProp], kind: "estimated" },
        { label: config.actualLabel, value: props[config.actualProp], kind: "actual" },
    ]
        .filter((row) => row.value)
        .map((row) => {
            const display = formatTime(row.value, airportCode, timezoneMode, airportsMap, baseUtcTime, "MM-DD HH:mm");
            return {
                ...row,
                display,
                diff: row.kind === "scheduled" ? "" : formatMinuteDiff(row.value, props[config.scheduledProp]),
            };
        });
}

function TimeTooltipContent({ rows }) {
    return (
        <div className="grid-core-time-tooltip">
            {rows.map((row) => (
                <div key={row.label} className="grid-core-time-tooltip-row">
                    <span className="grid-core-time-tooltip-label">{row.label}</span>
                    <span className="grid-core-time-tooltip-value">
                        {row.display.time}
                        {row.display.crossDayMark}
                    </span>
                    {(row.diff || row.display.label) && (
                        <span className="grid-core-time-tooltip-meta">
                            {[row.diff, row.display.label].filter(Boolean).join(" ")}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}

function TimeCell(props) {
    const {
        departureAirport,
        arrivalAirport,
        direction = "dep",
        timezoneMode,
        airportsMap,
        className,
    } = props;
    const config = DIRECTION_CONFIG[direction] || DIRECTION_CONFIG.dep;
    const { mode, airportsMap: resolvedAirportsMap } = normalizeTimezoneInput(timezoneMode, airportsMap);
    const airportCode = config.airportProp === "arrivalAirport" ? arrivalAirport : departureAirport;
    const primaryTime = pickPrimaryTime(config, props);
    const baseUtcTime = direction === "arr" ? getArrivalBaseUtcTime(props) : null;
    const display = formatTime(primaryTime.value, airportCode, mode, resolvedAirportsMap, baseUtcTime);
    const diff = primaryTime.kind === "scheduled" ? "" : formatMinuteDiff(primaryTime.value, primaryTime.scheduledValue);
    const metaText = [diff, display.crossDayMark].filter(Boolean).join(" ");
    const tooltipRows = buildTooltipRows(config, props, airportCode, mode, resolvedAirportsMap, baseUtcTime);
    const title = tooltipRows
        .map((row) => `${row.label} ${row.display.time}${row.display.crossDayMark ? ` ${row.display.crossDayMark}` : ""}${row.diff ? ` ${row.diff}` : ""}${row.display.label ? ` ${row.display.label}` : ""}`)
        .join("\n");
    const { showTooltip, hideTooltip } = useGridTooltipActions();
    const hasSharedTooltip = Boolean(showTooltip && tooltipRows.length > 0);

    const handleMouseEnter = (event) => {
        if (!hasSharedTooltip) return;
        showTooltip(<TimeTooltipContent rows={tooltipRows} />, event.currentTarget.getBoundingClientRect());
    };

    const handleMouseLeave = () => {
        hideTooltip?.();
    };

    return (
        <div
            className={joinClassNames(
                "grid-core-time-cell",
                `grid-core-time-cell-kind-${primaryTime.kind}`,
                display.error && "grid-core-time-cell-invalid",
                className,
            )}
            title={hasSharedTooltip ? undefined : title || undefined}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <span className="grid-core-time-cell-value">
                {primaryTime.label} {display.time}
            </span>
            {metaText && <span className="grid-core-time-cell-meta">{metaText}</span>}
        </div>
    );
}

export default TimeCell;
